// What the PRA case card and the acceptance page show for a PRA case (Stage 2 plan §D). Plain
// module with no Vue and no imports, so it runs under `node --test`. Labels are i18n keys.

// State -> chip colour: Aperta grey, CRD registrato blue, Radiazione richiesta amber, Radiata green.
export const PRA_STATE_COLORS = {
  open: "grey",
  crd_recorded: "blue",
  deregistration_requested: "amber",
  confirmed: "green",
};

// The stepper: Aperta · CRD · Richiesta · Ricevuta, and the step each state has reached.
export const PRA_STEPS = ["open", "crd", "request", "receipt"];
const REACHED_STEP = {
  open: 0,
  crd_recorded: 1,
  deregistration_requested: 2,
  confirmed: 3,
};

// The collapsed confirmed layout (decision Q2): only with PRA on and a confirmed dossier.
export function usesPraLayout({ praEnabled, dossierStatus }) {
  return praEnabled === true && dossierStatus === "confirmed";
}

// "← Storico veicoli" for a removed car with PRA on (DF10); otherwise the Stage 1 link.
export function backLink({ praEnabled, carStatus }) {
  if (praEnabled === true && carStatus === 2) {
    return {
      to: "/vehicle-entry?tab=history",
      key: "vehicle_entry.vehicles_history",
    };
  }
  return { to: "/vehicle-entry", key: "acceptance.page.back" };
}

// { key, color } of the state chip, or null for an unknown state.
export function stateChip(praCase) {
  const state = praCase && praCase.state;
  if (!PRA_STATE_COLORS[state]) return null;
  return {
    key: `acceptance.pra.states.${state}`,
    color: PRA_STATE_COLORS[state],
  };
}

// The "Pratica PRA" chips of both vehicle lists, from one by-cars row's `pra`: the state chip,
// "In sospeso" while on hold, and "In ritardo" while late and not confirmed (by-cars sends the raw
// late flag). [] (shown as "—") without a case or for an unknown state.
export function listChips(pra) {
  const chip = stateChip(pra);
  if (!chip) return [];
  const chips = [chip];
  if (pra.onHold === true) {
    chips.push({ key: "acceptance.pra.on_hold", color: "purple" });
  }
  if (pra.late === true && pra.state !== "confirmed") {
    chips.push({ key: "acceptance.pra.late_chip", color: "red" });
  }
  return chips;
}

// [{ key, status }], status "done" up to the reached step, "next" for the following one, "todo"
// after it. Every step is done once the case is confirmed.
export function stepperSteps(state) {
  const reached = REACHED_STEP[state] === undefined ? -1 : REACHED_STEP[state];
  return PRA_STEPS.map((key, index) => {
    let status = "todo";
    if (index <= reached) status = "done";
    else if (index === reached + 1) status = "next";
    return { key, status };
  });
}

// Which banners the card shows. late is the API's live flag; a confirmed case is never shown late.
export function bannerFlags(view) {
  const praCase = (view && view.praCase) || {};
  const confirmed = praCase.state === "confirmed";
  return {
    late: Boolean(view && view.late === true && !confirmed),
    confirmed,
    onHold: Boolean(praCase.activeHold),
  };
}

// The key of the opening line: opened at confirm, or by the activation script (backfill).
export function openingKey(praCase) {
  return praCase && praCase.openedVia === "backfill"
    ? "acceptance.pra.opened_backfill"
    : "acceptance.pra.opened_confirm";
}

// A failed GET /pra-case -> "no_case" when the confirmed dossier has no case yet (the backfill has
// not run), "error" for anything else.
export function praLoadState(err) {
  const res = err && err.response;
  return res &&
    res.status === 404 &&
    res.data &&
    res.data.code === "pra_case_not_found"
    ? "no_case"
    : "error";
}

// What the card shows: "draft" (opens at confirm), "loading", "no_case", "error" or "case".
// loadState is the page's last load result for a confirmed dossier.
export function cardState({ dossierStatus, loadState }) {
  if (dossierStatus === "draft") return "draft";
  return loadState || "loading";
}
